import { useState, useMemo, PropsWithChildren } from 'react'
import styles from './ProducerSliderLayout.module.scss'
import { ProducerSliderLayoutProps } from 'components/producer-slider-layout/types'
import { useTranslation } from 'next-i18next/pages'
import { getCapacityState, getEnergyNameI18Key, getProducerLocation } from 'utils/producers'
import PriceDisplay from 'features/simulation/components/proposition-card/price-display/PriceDisplay'
import { getProductToSelect, getProposition } from 'utils/price'
import classNames from 'classnames'
import FixedPriceDrawer from 'features/registration/components/registration-side-card/sales-prices-section/FixedPriceDrawer'
import { Button, Drawer, Icon, Img } from '@boltenergy-be/design-system'
import { Language } from 'types/language'
import { InputGroupKeys, SimulationFieldKeys } from 'store/customer/enums'
import { useStoreSelector } from 'hooks/store'
import useSelectInputGroup from 'hooks/useSelectInputGroup'
import { EnergyType } from 'types/producer'
import { SIMULATION_TYPES } from '@boltenergy-be/shared'
import { getProductName } from 'utils/products'
import { useGetLanguage } from 'hooks/useGetLanguage'

const ProducerSliderLayout = ({ producer, withPrice, children }: PropsWithChildren<ProducerSliderLayoutProps>) => {
  // Redux store
  const fixedPrices = useStoreSelector((store) => store.customer.fixedPrices)
  const { needsGas } = useSelectInputGroup(InputGroupKeys.DELIVERY_ADDRESS)
  const simulationInputs = useSelectInputGroup(InputGroupKeys.SIMULATION)
  const { simulation } = useStoreSelector((store) => store.customer.flows)
  const { confirmationFromPortalRegistration } = useStoreSelector((store) => store.marketing)

  // i18n
  const { t } = useTranslation(['simulationCard', 'common', 'producers'])
  const language = useGetLanguage()

  // Local state
  const [showMobileDrawer, setShowMobileDrawer] = useState<boolean>(false)
  const [showSalePriceMobileModal, setShowSalePriceMobileModal] = useState<boolean>(false)

  // Constants
  const proposition = getProposition()
  const product = getProductToSelect()
  const capacityState = useMemo(() => getCapacityState(producer, simulationInputs, true), [simulationInputs, producer])

  return (
    <>
      <div
        className={classNames(styles.card, { [styles['has-price']]: withPrice })}
        role="presentation"
        onClick={() => setShowMobileDrawer(true)}
      >
        <div className={styles['producer-container']}>
          <picture>
            <Img className={styles.thumbnail} alt={producer.name[language]} publicId={producer.images.profile?.publicId ?? ''} fill />
            <div className={styles['energy-types']}>
              {needsGas && <Icon name="gas" className={styles.gas} size={16} />}
              <Icon name="electricity" className={styles.electricity} size={16} />
            </div>
          </picture>

          <div className={styles.producer}>
            <small>
              {t(`energyTypeAndLocation.${producer.energyType === EnergyType.BIO ? 'bio' : 'other'}`, {
                energyType:
                  language === Language.FRENCH
                    ? t(getEnergyNameI18Key(producer.energyType)).toLowerCase()
                    : t(getEnergyNameI18Key(producer.energyType)),
                location: getProducerLocation(language, producer?.location)
              })}
            </small>
            <h3>{producer.name[language]}</h3>
          </div>

          <span className={classNames(styles['open-icon'], { [styles.warning]: capacityState.soldOut })}>
            {capacityState.soldOut ? <Icon name="exclamation" size={20} className={styles.warning} /> : <Icon name="chevronUp" size={20} />}
          </span>
        </div>

        {withPrice && simulationInputs.chosenSimulationType !== SIMULATION_TYPES.eav && (
          <>
            <hr />
            <div className={styles['price-container']}>
              {!confirmationFromPortalRegistration && simulationInputs.chosenSimulationType === SIMULATION_TYPES.fixedAmount ? (
                <Button
                  variant="layered"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowSalePriceMobileModal(true)
                  }}
                >
                  {t('common:change', 'Wijzig')}
                </Button>
              ) : (
                <span className={styles.product}>{getProductName(product, language, true)}</span>
              )}
              <PriceDisplay
                forcedAmount={simulationInputs[SimulationFieldKeys.FORCED_AMOUNT] ?? undefined}
                proposition={simulationInputs.chosenSimulationType !== SIMULATION_TYPES.fixedAmount ? proposition : undefined}
                fixedPrices={simulationInputs.chosenSimulationType === SIMULATION_TYPES.fixedAmount ? fixedPrices : undefined}
                label={t('perMonth', '/maand')}
                className={styles.price}
                isLoading={simulation.isLoading}
              />
            </div>
          </>
        )}
      </div>

      <Drawer
        isOpen={showMobileDrawer}
        onRequestClose={() => setShowMobileDrawer(false)}
        customIcon="chevronDown"
        className={styles['producer-drawer']}
      >
        <Img publicId={producer.images.profile?.publicId ?? ''} alt={producer.name[language]} fill />
        {children}
      </Drawer>

      <FixedPriceDrawer onRequesteClose={() => setShowSalePriceMobileModal(false)} isOpen={showSalePriceMobileModal} />
    </>
  )
}

export default ProducerSliderLayout
